import { useEffect, useState } from 'react';
import { sprintf, __ } from '@wordpress/i18n';
import { registerPaymentMethod } from '@woocommerce/blocks-registry';
import { decodeEntities } from '@wordpress/html-entities';
import { getSetting } from '@woocommerce/settings';
import { usePaymentInputs } from './hooks';
import { PaymentInputsWrapper, TermsCheckbox, AchForm, GiftForm } from './components';
import images from './images';
import { PAYMENT_STORE_KEY } from '@woocommerce/block-data';
import { subscribe, select, dispatch, use } from '@wordpress/data';
import { extensionCartUpdate } from '@woocommerce/blocks-checkout';
import styled from 'styled-components';

const settings = getSetting( 'wc_valorpay_data', {} );
if ( settings && ! settings.is_not_blocked ) {
	dispatch( 'core/notices' ).createErrorNotice(
		__(
			'Valor Pay is disabled due to multiple payment failures.',
			'wc-valorpay'
		),
		{
			context: 'wc/checkout/payments', // Display the notice in the payments context.
		}
	);
}
if ( settings && settings.card_type_allowed ) {
	let noticeMsg = null;
	if ( 'debit' === settings.card_type_allowed ) {
		noticeMsg = __( 'Only debit card allowed', 'wc-valorpay' );
	}
	if ( 'credit' === settings.card_type_allowed ) {
		noticeMsg = __( 'Only credit card allowed', 'wc-valorpay' );
	}
	if ( noticeMsg ) {
		dispatch( 'core/notices' ).createInfoNotice( noticeMsg, {
			context: 'wc/checkout/payments', // Display the notice in the payments context.
		} );
	}
}
subscribe( function () {
	const paymentMethodState = select( PAYMENT_STORE_KEY );
	const chosenPaymentMethod = paymentMethodState.getActivePaymentMethod();
	const savedPaymentMethods = paymentMethodState.getSavedPaymentMethods();
	const selectedPaymentTokenId = +paymentMethodState.getActiveSavedToken();
	let selectedCardType = '';
	if ( selectedPaymentTokenId ) {
		const foundMethod = savedPaymentMethods.cc.find(
			( method ) =>
				method.tokenId === selectedPaymentTokenId &&
				method.method.gateway === 'wc_valorpay'
		);
		if ( foundMethod ) {
			selectedCardType = foundMethod.method.card_type;
		}
	}

	extensionCartUpdate( {
		namespace: 'wc-valorpay-fee-update',
		data: {
			action_type: 'update_payment',
			payment_method: chosenPaymentMethod,
			card_type: selectedCardType,
		},
	} );
}, PAYMENT_STORE_KEY );

const defaultLabel = __( 'Valor Pay', 'wc-valorpay' );

const label = decodeEntities( settings.title ) || defaultLabel;

// Tab Button Styled Component
const TabButton = styled.button`
  	flex: 1;  // Ensures buttons take equal width
	padding: 20px;
	background-color: transparent;  // Ensure button background doesn't change
	color: inherit;  // Use text color from the theme
	border: none;
	cursor: pointer;
	text-align: center;
	border-bottom: ${props => (props.active ? '2px solid' : 'none')};
  	border-color: ${props => (props.active ? '#007BFF' : 'transparent')};  // Use theme-based color or default
	&:hover {
		opacity: 0.8;
	}
`;

// Tab Container Styled Component
const TabContainer = styled.div`
	display: flex;
	width: 100%;
	padding: 10px;
`;

// Tab Content Styled Component
const TabContent = styled.div`
	background-color: #f9f9f9;
	border: 1px solid #ccc;
	border-radius: 5px;
	margin-top: 10px;
	padding: 20px;
`;

const Tabs = (props) => {
	const [activeTab, setActiveTab] = useState(0);

	let tabTitles = [ __('Card','wc-valorpay') ];
	let tabContents = [ <CardForm {...props}/> ];

	if ( settings.is_ach_enable ) {
		tabTitles.push(__('ACH','wc-valorpay'));
		tabContents.push(<AchForm {...props}/>);
	}
	if ( settings.is_gift_card_enable ) {
		tabTitles.push(__('Gift Card','wc-valorpay'));
		tabContents.push(<GiftForm {...props}/>);
	}

	return (
	<>
		<TabContainer>
			{tabTitles.map((title, index) => (
				<TabButton
					key={index}
					active={activeTab === index}
					onClick={() => setActiveTab(index)}
					type="button"
				>
					{title}
				</TabButton>
			))}
		</TabContainer>
		<TabContent>
			{tabContents[activeTab]}
		</TabContent>
	</>
	);
};

/**
 * Content component
 */
const CardForm = ( props ) => {
	const [ isFetchingCardType, setIsFetchingCardType ] = useState( false );
	const [ isInitPay, setIsInitPay ] = useState( false );
	let paymentFields = [ 'cardNumber', 'expiryDate', 'cvc' ];
	if (
		settings.avs_type === 'zipandaddress' ||
		settings.avs_type === 'zip'
	) {
		paymentFields.push( 'zip' );
	}
	if (
		settings.avs_type === 'zipandaddress' ||
		settings.avs_type === 'address'
	) {
		paymentFields.push( 'streetaddress' );
	}
	if ( settings.is_enable_l2_l3 ) {
	paymentFields.push( 'poNumber' );
	paymentFields.push( 'custVatNumber' );
	paymentFields.push( 'vatInvoiceNumber' );
	}
	const {
		getCardNumberProps,
		getExpiryDateProps,
		getCVCProps,
		getCardImageProps,
		getZIPProps,
		getStreetAddressProps,
		getPONumberProps,
		getcustVatNumberProps,
		getvatInvoiceNumberProps,
		wrapperProps,
		meta: {
			erroredInputs,
			isTouched,
			focused,
			cardNumberField,
			expiryDateField,
			cvcField,
			zipField,
			addressField,
			poNumberField,
			custVatNumberField,
			vatInvoiceNumberField,
			l2l3ErroredInputs
		},
	} = usePaymentInputs( {
		avsType: settings.avs_type,
		setIsFetchingCardType,
		paymentFields: paymentFields,
		paymentFields: paymentFields.concat(['poNumber']),
		paymentFields: paymentFields.concat(['custVatNumber']),
		paymentFields: paymentFields.concat(['vatInvoiceNumber']),
	} );
	const {
		components: { LoadingMask },
		eventRegistration,
		emitResponse,
		paymentStatus,
	} = props;
	const [ isTermsChecked, setIsTermsChecked ] = useState( true );
	const { onPaymentSetup, onCheckoutAfterProcessingWithError } =
		eventRegistration;
	const handleCheckboxChange = () => {
		setIsTermsChecked((prev) => !prev);
	};
	useEffect(() => {
		extensionCartUpdate( {
            namespace: 'wc-valorpay-fee-update',
            data: {
                action_type: 'update_payment_type',
                payment_type: "",
            },
        } );
	}, []);
	useEffect( () => {
		const unsubscribe = onPaymentSetup( async () => {
			let isValid = true;
			for ( let errorInput in erroredInputs ) {
				if ( erroredInputs[ errorInput ] ) {
					isValid = false;
					break;
				}
			}
			if ( isValid ) {
				let paymentMethodData = {
					'wc_valorpay-card-number': cardNumberField.current.value,
					'wc_valorpay-card-expiry': expiryDateField.current.value,
					'wc_valorpay-card-cvc': cvcField.current.value,
					valorpay_avs_type: settings.avs_type,
					valorpay_terms: isTermsChecked,
				};
				if (
					settings.avs_type === 'zipandaddress' ||
					settings.avs_type === 'zip'
				) {
					paymentMethodData.valorpay_avs_zip = zipField.current.value;
				}
				if ( settings.is_enable_l2_l3 ) {
					paymentMethodData.valorpay_po_number = poNumberField.current.value;
					paymentMethodData.valorpay_custvat_number = custVatNumberField.current.value;
					paymentMethodData.valorpay_vatinvoice_number = vatInvoiceNumberField.current.value;
				}
				if (
					settings.avs_type === 'zipandaddress' ||
					settings.avs_type === 'address'
				) {
					paymentMethodData.valorpay_avs_street =
						addressField.current.value;
				}
				return {
					type: emitResponse.responseTypes.SUCCESS,
					meta: {
						paymentMethodData: {
							...paymentMethodData,
						},
					},
				};
			}
			setIsInitPay( true );
			return {
				type: emitResponse.responseTypes.ERROR,
				message: __(
					'Please provide payment information',
					'wc-valorpay'
				),
			};
		} );

		const unsubscribeErrorMsg = onCheckoutAfterProcessingWithError(
			( { processingResponse } ) => {
				if ( processingResponse?.paymentDetails?.errorMessage ) {
					return {
						type: emitResponse.responseTypes.ERROR,
						message: processingResponse.paymentDetails.errorMessage,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				}
				// so we don't break the observers.
				return true;
			}
		);
		// Unsubscribes when this component is unmounted.
		return () => {
			unsubscribe();
			unsubscribeErrorMsg();
		};
	}, [
		emitResponse.responseTypes.ERROR,
		emitResponse.responseTypes.SUCCESS,
		onPaymentSetup,
		erroredInputs,
		onCheckoutAfterProcessingWithError,
	] );
	return (
		<>
			{ settings.is_sandbox_mode && (
				<p>
					{ __(
						'TEST MODE ENABLED. Use test card number 4111111111111111 with 999 as CVC and a future expiration date.',
						'wc-valorpay'
					) }
				</p>
			) }
			<LoadingMask
				showSpinner={ isFetchingCardType }
				isLoading={ isFetchingCardType }
				screenReaderLabel={ __(
					'Fetching Card Type...',
					'wc-valorpay'
				) }
			>
				<PaymentInputsWrapper
					{ ...wrapperProps }
					isInitPay={ isInitPay }
				>
					<svg { ...getCardImageProps( { images } ) } />
					<input { ...getCardNumberProps() } />
					<input { ...getExpiryDateProps() } />
					<input { ...getCVCProps() } />

					{ ( settings.avs_type === 'zipandaddress' ||
						settings.avs_type === 'zip' ) && (
						<input { ...getZIPProps() } />
					) }
					{ ( settings.avs_type === 'zipandaddress' ||
						settings.avs_type === 'address' ) && (
						<input { ...getStreetAddressProps() } />
					) }
					</PaymentInputsWrapper>
					{ settings.is_enable_l2_l3 && (
					<>
						<div style={{marginTop: '10px'}}>
							<label htmlFor="poNumber" style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>PO Number</label>
							<PaymentInputsWrapper
								error={l2l3ErroredInputs.poNumber}
								focused={focused === 'poNumber'}
								isTouched={isTouched}
								isInitPay={ isInitPay }
							>
								<input { ...getPONumberProps() } />
							</PaymentInputsWrapper>
						</div>
						<div style={{marginTop: '10px'}}>
							<label htmlFor="custVatNumber" style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>Customer VAT Number</label>
							<PaymentInputsWrapper
								error={l2l3ErroredInputs.custVatNumber}
								focused={focused === 'custVatNumber'}
								isTouched={isTouched}
								isInitPay={ isInitPay }
							>
								<input { ...getcustVatNumberProps() } />
							</PaymentInputsWrapper>
						</div>
						<div style={{marginTop: '10px'}}>
							<label htmlFor="vatInvoiceNumber" style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>VAT Invoice Number</label>
							<PaymentInputsWrapper
								error={l2l3ErroredInputs.vatInvoiceNumber}
								focused={focused === 'vatInvoiceNumber'}
								isTouched={isTouched}
								isInitPay={ isInitPay }
							>
								<input { ...getvatInvoiceNumberProps() } />
							</PaymentInputsWrapper>
						</div>
					</>
				) }
			</LoadingMask>
			{settings.is_terms_conditions_enable && <TermsCheckbox
				id="valorpay-terms-new-card"
				checked={ isTermsChecked }
				onChange={ handleCheckboxChange }
				label={
					<span
						dangerouslySetInnerHTML={ {
							__html: settings.terms_label,
						} }
					/>
				}
			/>}
		</>
	);
};

const SavedToken = ( props ) => {
	const [ isInitPay, setIsInitPay ] = useState( false );
	const [ customerVAT, setCustomerVAT ] = useState(''); 
	let paymentFields = [];
	if (
		settings.avs_type === 'zipandaddress' ||
		settings.avs_type === 'zip'
	) {
		paymentFields.push( 'zip' );
	}
	if (
		settings.avs_type === 'zipandaddress' ||
		settings.avs_type === 'address'
	) {
		paymentFields.push( 'streetaddress' );
	}
	if ( settings.is_enable_l2_l3 ) {
		paymentFields.push( 'poNumber' );
		paymentFields.push( 'custVatNumber' );
		paymentFields.push( 'vatInvoiceNumber' );
	}
	const {
		getZIPProps,
		getStreetAddressProps,
		getPONumberProps,
		getcustVatNumberProps,
		getvatInvoiceNumberProps,

		wrapperProps,
		meta: { erroredInputs, focused, isTouched, zipField, addressField, poNumberField, custVatNumberField, vatInvoiceNumberField, l2l3ErroredInputs },
	} = usePaymentInputs( {
		avsType: settings.avs_type,
		paymentFields: paymentFields,
	} );
	const { eventRegistration, emitResponse, paymentStatus, token } = props;
	const { onPaymentSetup, onCheckoutAfterProcessingWithError } =
		eventRegistration;
	const [ isTermsChecked, setIsTermsChecked ] = useState( true );
	const handleCheckboxChange = () => {
		setIsTermsChecked((prev) => !prev);
	};
	useEffect(() => {
		extensionCartUpdate( {
            namespace: 'wc-valorpay-fee-update',
            data: {
                action_type: 'update_payment_type',
                payment_type: "",
            },
        } );
	}, []);
	useEffect( () => {
		const unsubscribe = onPaymentSetup( async () => {
			let isValid = true;
			for ( let errorInput in erroredInputs ) {
				if ( erroredInputs[ errorInput ] ) {
					isValid = false;
					break;
				}
			}
			if ( isValid ) {
				let paymentMethodData = {
					'wc-wc_valorpay-payment-token': token,
					valorpay_avs_type: settings.avs_type,
					valorpay_terms: isTermsChecked,
				};
				if (
					settings.avs_type === 'zipandaddress' ||
					settings.avs_type === 'zip'
				) {
					paymentMethodData.valorpay_avs_zip = zipField.current.value;
				}
				if (
					settings.avs_type === 'zipandaddress' ||
					settings.avs_type === 'address'
				) {
					paymentMethodData.valorpay_avs_street =
						addressField.current.value;
				}
				if ( settings.is_enable_l2_l3 ) {
					paymentMethodData.valorpay_po_number = poNumberField.current.value;
					paymentMethodData.valorpay_custvat_number = custVatNumberField.current.value;
					paymentMethodData.valorpay_vatinvoice_number = vatInvoiceNumberField.current.value;
				}
				return {
					type: emitResponse.responseTypes.SUCCESS,
					meta: {
						paymentMethodData: {
							...paymentMethodData,
						},
					},
				};
			}
			setIsInitPay( true );
			return {
				type: emitResponse.responseTypes.ERROR,
				message: __(
					'Please provide payment information',
					'wc-valorpay'
				),
			};
		} );
		const unsubscribeErrorMsg = onCheckoutAfterProcessingWithError(
			( { processingResponse } ) => {
				if ( processingResponse?.paymentDetails?.errorMessage ) {
					return {
						type: emitResponse.responseTypes.ERROR,
						message: processingResponse.paymentDetails.errorMessage,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				}
				// so we don't break the observers.
				return true;
			}
		);
		// Unsubscribes when this component is unmounted.
		return () => {
			unsubscribe();
			unsubscribeErrorMsg();
		};
	}, [
		emitResponse.responseTypes.ERROR,
		emitResponse.responseTypes.SUCCESS,
		onPaymentSetup,
		erroredInputs,
		token,
	] );
	return (
		<>
			{ settings.avs_type !== 'none' && (
				<>
					<PaymentInputsWrapper
						{ ...wrapperProps }
						isInitPay={ isInitPay }
					>
						{ ( settings.avs_type === 'zipandaddress' ||
							settings.avs_type === 'zip' ) && (
							<input { ...getZIPProps() } />
						) }
						{ ( settings.avs_type === 'zipandaddress' ||
							settings.avs_type === 'address' ) && (
							<input { ...getStreetAddressProps() } />
						) }
					</PaymentInputsWrapper>
				</>
			) }
			{ settings.is_enable_l2_l3 && (
					<>
						<div style={{marginTop: '10px '}}>
							<label htmlFor="poNumber" style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>PO Number</label>
							<PaymentInputsWrapper
								error={l2l3ErroredInputs.poNumber}
								focused={focused === 'poNumber'}
								isTouched={isTouched}
								isInitPay={ isInitPay }
							>
								<input { ...getPONumberProps() } />
							</PaymentInputsWrapper>
						</div>
						<div style={{marginTop: '10px'}}>
							<label htmlFor="custVatNumber" style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>Customer VAT Number</label>
							<PaymentInputsWrapper
								error={l2l3ErroredInputs.custVatNumber}
								focused={focused === 'custVatNumber'}
								isTouched={isTouched}
								isInitPay={ isInitPay }
							>
								<input { ...getcustVatNumberProps() } />
							</PaymentInputsWrapper>
						</div>
						<div style={{marginTop: '10px'}}>
							<label htmlFor="vatInvoiceNumber" style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>VAT Invoice Number</label>
							<PaymentInputsWrapper
								error={l2l3ErroredInputs.vatInvoiceNumber}
								focused={focused === 'vatInvoiceNumber'}
								isTouched={isTouched}
								isInitPay={ isInitPay }
							>
								<input { ...getvatInvoiceNumberProps() } />
							</PaymentInputsWrapper>
						</div>
					</>
				) }
			{settings.is_terms_conditions_enable && <TermsCheckbox
				id="valorpay-terms-saved-token"
				checked={ isTermsChecked }
				onChange={ handleCheckboxChange }
				label={
					<span
						dangerouslySetInnerHTML={ {
							__html: settings.terms_label,
						} }
					/>
				}
			/>}
		</>
	);
};

/**
 * Label component
 *
 * @param {*} props Props from payment API.
 */
const Label = ( props ) => {
	const { PaymentMethodLabel, PaymentMethodIcons } = props.components;
	return (
		<>
			<PaymentMethodLabel text={ label } />
			<PaymentMethodIcons icons={ settings.card_types } align="right" />
		</>
	);
};

const Content =  (props ) => {
	if ( !settings.is_ach_enable && !settings.is_gift_card_enable ) {
		return <CardForm {...props} />
	}
	return <Tabs {...props} />
}

/**
 * ValorPay payment method config object.
 */
const ValorPay = {
	name: 'wc_valorpay',
	label: <Label />,
	gatewayId: 'wc_valorpay',
	content: <Content />,
	edit: <Content />,
	canMakePayment: () => settings.is_not_blocked,
	savedTokenComponent: <SavedToken />,
	ariaLabel: label,
	supports: {
		features: settings.supports,
		showSaveOption: settings.is_save_to_account_enable,
	},
};

registerPaymentMethod( ValorPay );